import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { configureApiClient } from '../api/client'
import { useToast } from '../components/ui/Toast'
import type { AuthTokens, User } from '../types/auth'

interface AuthContextValue {
  user: User | null
  accessToken: string | null
  login: (email: string, password: string) => Promise<void>
  loginWithToken: (token: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  setAccessToken: (token: string | null) => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const toast = useToast()

  // Expose accessToken via ref so apiFetch can read it without a stale closure
  const tokenRef = useRef<string | null>(null)
  tokenRef.current = accessToken

  const fetchMe = useCallback(async (token: string): Promise<void> => {
    const res = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to fetch user')
    const data = (await res.json()) as User
    setUser(data)
  }, [navigate])

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) return null
    const data = (await res.json()) as AuthTokens
    setAccessToken(data.access_token)
    tokenRef.current = data.access_token
    return data.access_token
  }, [])

  // Wire up apiFetch with live token accessor and unauthenticated handler
  useEffect(() => {
    configureApiClient(
      () => tokenRef.current,
      setAccessToken,
      () => {
        toast.error('Your session has expired. Please log in again.')
        void navigate('/login', { replace: true })
      },
    )
  }, [navigate, toast])

  // Restore session on mount via httpOnly refresh cookie
  useEffect(() => {
    let cancelled = false
    const restore = async () => {
      const token = await refreshAccessToken()
      if (cancelled) return
      if (token) {
        try {
          await fetchMe(token)
        } catch {
          setAccessToken(null)
        }
      }
      setIsLoading(false)
    }
    void restore()
    return () => { cancelled = true }
  }, [refreshAccessToken, fetchMe])

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const err = (await res.json()) as { detail?: string }
        throw new Error(err.detail ?? 'Invalid credentials')
      }
      const data = (await res.json()) as AuthTokens
      setAccessToken(data.access_token)
      tokenRef.current = data.access_token
      await fetchMe(data.access_token)
    },
    [fetchMe],
  )

  const loginWithToken = useCallback(async (token: string): Promise<void> => {
    setAccessToken(token)
    tokenRef.current = token
    await fetchMe(token)
  }, [fetchMe])

  const logout = useCallback(async (): Promise<void> => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    setUser(null)
    setAccessToken(null)
    tokenRef.current = null
  }, [])

  const refreshUser = useCallback(async (): Promise<void> => {
    if (tokenRef.current) await fetchMe(tokenRef.current)
  }, [fetchMe])

  return (
    <AuthContext.Provider
      value={{ user, accessToken, login, loginWithToken, logout, refreshUser, setAccessToken, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
